import { useEffect, useRef, useState, useCallback } from 'react';
import { api } from '../../lib/api.js';

interface Props {
  lessonId: string;
  lessonTitle: string;
  onClose: () => void;
}

export const STARTER_TEMPLATE = `<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8">
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }

  body {
    background: #0f1112;
    color: #e0e0e0;
    font-family: 'Segoe UI', system-ui, sans-serif;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100vh;
    gap: 16px;
    padding: 24px;
    text-align: center;
    user-select: none;
  }

  h2 {
    font-size: 1.15rem;
    color: #ffffff;
    font-weight: 700;
    letter-spacing: 0.01em;
  }

  p {
    font-size: 0.82rem;
    color: #888;
    max-width: 300px;
    line-height: 1.55;
  }

  .btn {
    padding: 10px 32px;
    background: #e53935;
    color: #fff;
    border: none;
    border-radius: 6px;
    font-size: 0.88rem;
    font-weight: 700;
    cursor: pointer;
    letter-spacing: 0.04em;
    transition: background 0.15s, transform 0.1s;
  }
  .btn:hover { background: #c62828; }
  .btn:active { transform: scale(0.96); }

  #feedback {
    font-size: 0.8rem;
    font-weight: 600;
    min-height: 18px;
    letter-spacing: 0.02em;
  }
  .pass { color: #4caf50; }
  .fail { color: #ef5350; }
</style>
</head>
<body>

  <h2>Challenge Title</h2>
  <p>Describe exactly what the user needs to do to pass this challenge.</p>

  <button class="btn" onclick="attempt()">Try it</button>
  <div id="feedback"></div>

<script>
  function attempt() {
    // Replace this logic with your real challenge check
    const passed = Math.random() > 0.45;

    const fb = document.getElementById('feedback');

    if (passed) {
      fb.textContent = '\\u2713 Perfect! Challenge complete.';
      fb.className = 'pass';

      // THIS LINE IS REQUIRED - do not remove it
      // It tells PVP Academy the user passed this challenge
      setTimeout(() => {
        window.parent.postMessage({ passed: true }, '*');
      }, 900);

    } else {
      fb.textContent = '\\u2717 Not quite - try again!';
      fb.className = 'fail';
    }
  }
</script>

</body>
</html>`;

type Tab = 'editor' | 'test';

export default function ChallengeEditor({ lessonId, lessonTitle, onClose }: Props) {
  const [html, setHtml] = useState('');
  const [loadingChallenge, setLoadingChallenge] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saved' | 'error'>('idle');
  const [activeTab, setActiveTab] = useState<Tab>('editor');
  const [passEventFired, setPassEventFired] = useState(false);
  const [previewKey, setPreviewKey] = useState(0);
  const [liveHtml, setLiveHtml] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load existing challenge from backend
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get<{ challengeHtml: string | null }>(
  `/api/lessons/${lessonId}/challenge`
);
        const initial = data.challengeHtml ?? STARTER_TEMPLATE;
        setHtml(initial);
        setLiveHtml(initial);
      } catch {
        setHtml(STARTER_TEMPLATE);
        setLiveHtml(STARTER_TEMPLATE);
      } finally {
        setLoadingChallenge(false);
      }
    })();
  }, [lessonId]);

  // Debounce live preview updates
  const handleHtmlChange = useCallback((value: string) => {
    setHtml(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setLiveHtml(value), 600);
  }, []);

  // Listen for postMessage pass event from any iframe
  useEffect(() => {
    const handler = (e: MessageEvent) => {
      if (e.data?.passed === true) {
        setPassEventFired(true);
        setTimeout(() => setPassEventFired(false), 3500);
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setSaveStatus('idle');
    try {
      await api.patch(
  `/api/lessons/${lessonId}/challenge`,
  { challengeHtml: html }
);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2500);
    } catch {
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } finally {
      setSaving(false);
    }
  };

  const handleResetTemplate = () => {
    if (!confirm('Reset to the starter template? Your current code will be lost.')) return;
    setHtml(STARTER_TEMPLATE);
    setLiveHtml(STARTER_TEMPLATE);
  };

  const handleClearChallenge = async () => {
    if (!confirm('Remove the challenge from this lesson entirely?')) return;
    try {
      await api.patch(
  `/api/lessons/${lessonId}/challenge`,
  { challengeHtml: null }
);
      onClose();
    } catch {
      alert('Failed to clear challenge');
    }
  };

  const handleTabKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key !== 'Tab') return;
    e.preventDefault();
    const el = e.currentTarget;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const next = html.substring(0, start) + '  ' + html.substring(end);
    setHtml(next);
    setLiveHtml(next);
    setTimeout(() => el.setSelectionRange(start + 2, start + 2), 0);
  };

  if (loadingChallenge) {
    return (
      <div className="fixed inset-0 z-50 bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-on-surface-variant text-sm animate-pulse">Loading challenge editor...</div>
      </div>
    );
  }

  const saveLabel =
    saving ? 'Saving...' :
    saveStatus === 'saved' ? '✓ Saved' :
    saveStatus === 'error' ? '✗ Failed' :
    'Save challenge';

  const saveBg =
    saveStatus === 'saved' ? 'bg-[#2e7d32]' :
    saveStatus === 'error' ? 'bg-[#b71c1c]' :
    'bg-[#e53935] hover:bg-[#c62828]';

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-[#0a0a0a]">

      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-2.5 bg-[#111] border-b border-white/[0.07] shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <button
            onClick={onClose}
            className="flex items-center gap-1.5 text-xs text-on-surface-variant hover:text-on-surface transition-colors shrink-0"
          >
            ← Back
          </button>
          <div className="w-px h-4 bg-white/10 shrink-0" />
          <span className="text-xs font-semibold text-white/90 shrink-0">Challenge Editor</span>
          <span className="text-xs text-on-surface-variant truncate hidden sm:block">— {lessonTitle}</span>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {passEventFired && (
            <div className="flex items-center gap-1.5 px-2.5 py-1 bg-[rgba(76,175,80,0.15)] border border-[#4caf50]/30 rounded text-[11px] text-[#4caf50] font-semibold animate-pulse">
              <span className="w-1.5 h-1.5 rounded-full bg-[#4caf50]" />
              Pass event received
            </div>
          )}
          <button
            onClick={handleResetTemplate}
            className="text-xs px-3 py-1.5 border border-white/10 rounded text-on-surface-variant hover:text-on-surface hover:border-white/20 transition-colors"
          >
            Reset template
          </button>
          <button
            onClick={handleClearChallenge}
            className="text-xs px-3 py-1.5 border border-red-500/20 rounded text-red-400/60 hover:text-red-400 hover:border-red-500/40 transition-colors"
          >
            Remove
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className={`text-xs px-4 py-1.5 ${saveBg} text-white rounded font-bold transition-colors disabled:opacity-60`}
          >
            {saveLabel}
          </button>
        </div>
      </div>

      {/* Tab bar */}
      <div className="flex items-center px-4 bg-[#0f0f0f] border-b border-white/[0.06] shrink-0">
        {(['editor', 'test'] as Tab[]).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2.5 text-xs font-semibold uppercase tracking-wider border-b-2 transition-colors ${
              activeTab === tab
                ? 'border-[#e53935] text-white'
                : 'border-transparent text-on-surface-variant hover:text-on-surface'
            }`}
          >
            {tab === 'editor' ? '⌨  Editor + Preview' : '▶  Test Area'}
          </button>
        ))}
        <div className="ml-auto text-[10px] text-on-surface-variant/40 pb-2.5 hidden sm:block">
          Tab = 2 spaces · preview auto-updates 600ms after typing
        </div>
      </div>

      {/* Editor + Preview tab */}
      {activeTab === 'editor' && (
        <div className="flex flex-1 overflow-hidden">

          {/* Code editor */}
          <div className="flex flex-col w-1/2 border-r border-white/[0.06]">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-[#0d0d0d] border-b border-white/[0.05] shrink-0">
              <div className="flex gap-1">
                <span className="w-2.5 h-2.5 rounded-full bg-[#e53935]/80" />
                <span className="w-2.5 h-2.5 rounded-full bg-[#ffbf00]/50" />
                <span className="w-2.5 h-2.5 rounded-full bg-[#4caf50]/40" />
              </div>
              <span className="text-[10px] text-on-surface-variant/60 font-mono ml-1">challenge.html</span>
              <span className="ml-auto text-[10px] text-on-surface-variant/40 font-mono">
                {html.split('\n').length} lines
              </span>
            </div>
            <textarea
              value={html}
              onChange={e => handleHtmlChange(e.target.value)}
              onKeyDown={handleTabKey}
              spellCheck={false}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              className="flex-1 w-full bg-[#080808] text-[#d4d4d4] font-mono text-[11.5px] p-4 resize-none outline-none leading-[1.7] caret-[#e53935]"
              style={{ tabSize: 2 }}
            />
          </div>

          {/* Live preview */}
          <div className="flex flex-col w-1/2">
            <div className="flex items-center justify-between px-3 py-1.5 bg-[#0d0d0d] border-b border-white/[0.05] shrink-0">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-[#4caf50]/70" />
                <span className="text-[10px] text-on-surface-variant/60 font-mono">live preview</span>
              </div>
              <button
                onClick={() => { setLiveHtml(html); setPreviewKey(k => k + 1); }}
                className="text-[10px] text-on-surface-variant/50 hover:text-on-surface/80 transition-colors"
              >
                ↺ Refresh
              </button>
            </div>
            <iframe
              key={previewKey}
              srcDoc={liveHtml}
              sandbox="allow-scripts"
              className="flex-1 w-full"
              style={{ border: 'none', background: '#0f1112' }}
              title="Challenge live preview"
            />
          </div>
        </div>
      )}

      {/* Test area tab */}
      {activeTab === 'test' && (
        <div className="flex-1 overflow-auto p-6">
          <div className="max-w-2xl mx-auto space-y-4">

            {/* Info */}
            <div className="flex items-start gap-3 p-3.5 bg-[rgba(229,57,53,0.06)] border border-[#e53935]/20 rounded-lg">
              <span className="text-base mt-0.5 shrink-0">🧪</span>
              <div>
                <div className="text-xs font-semibold text-white/80 mb-0.5">Test Area</div>
                <div className="text-[11px] text-on-surface-variant leading-relaxed">
                  This is exactly what users will see. Interact below. When{' '}
                  <code className="text-[#e53935] font-mono bg-[#e53935]/10 px-1 rounded text-[10px]">
                    postMessage({'{ passed: true }'})
                  </code>{' '}
                  fires, the green banner appears.
                </div>
              </div>
            </div>

            {/* Pass event indicator */}
            {passEventFired ? (
              <div className="flex items-center gap-3 p-4 bg-[rgba(76,175,80,0.08)] border border-[#4caf50]/30 rounded-lg">
                <div className="w-8 h-8 rounded-full bg-[#4caf50]/20 flex items-center justify-center shrink-0 text-base">✓</div>
                <div>
                  <div className="text-sm font-semibold text-[#4caf50]">Pass event received!</div>
                  <div className="text-xs text-on-surface-variant mt-0.5">
                    Your challenge correctly fired{' '}
                    <code className="font-mono text-[10px]">postMessage({'{ passed: true }'})</code>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2 px-3 py-2 bg-white/[0.03] border border-white/[0.06] rounded text-[11px] text-on-surface-variant">
                <span className="w-1.5 h-1.5 rounded-full bg-on-surface-variant/40 animate-pulse shrink-0" />
                Waiting for pass event...
              </div>
            )}

            {/* The actual challenge iframe */}
            <div className="rounded-xl overflow-hidden border border-white/[0.08]">
              <div className="flex items-center gap-2 px-3 py-2 bg-[#111] border-b border-white/[0.06]">
                <span className="w-1.5 h-1.5 rounded-full bg-[#e53935]" />
                <span className="text-[10px] text-on-surface-variant/60 uppercase tracking-wider font-semibold">
                  Practice Challenge — {lessonTitle}
                </span>
                <button
                  onClick={() => setPreviewKey(k => k + 1)}
                  className="ml-auto text-[10px] text-on-surface-variant/50 hover:text-on-surface/80 transition-colors"
                >
                  ↺ Restart
                </button>
              </div>
              <iframe
                key={`test-${previewKey}`}
                srcDoc={html}
                sandbox="allow-scripts"
                style={{ width: '100%', height: '400px', border: 'none', background: '#0f1112', display: 'block' }}
                title="Challenge test"
              />
            </div>

            {/* Pre-save checklist */}
            <div className="p-4 bg-[#111] border border-white/[0.06] rounded-lg space-y-2.5">
              <div className="text-[10px] font-semibold text-white/50 uppercase tracking-wider mb-3">Pre-save checklist</div>
              {[
                'Challenge is interactive (not just a button that always passes)',
                'Failure path gives clear feedback to the user',
                'postMessage({ passed: true }) fires ONLY on a real success',
                'Challenge fits inside 400px height without scrolling',
                'Dark background (#0f1112) matches the rest of the site',
              ].map((item, i) => (
                <label key={i} className="flex items-start gap-2.5 cursor-pointer group">
                  <input type="checkbox" className="mt-0.5 accent-[#e53935] shrink-0 cursor-pointer" />
                  <span className="text-xs text-on-surface-variant group-hover:text-on-surface transition-colors leading-relaxed">{item}</span>
                </label>
              ))}
            </div>

            <button
              onClick={handleSave}
              disabled={saving}
              className={`w-full py-3 ${saveBg} text-white rounded-lg text-sm font-bold tracking-wide disabled:opacity-60 transition-colors`}
            >
              {saveLabel}
            </button>

          </div>
        </div>
      )}

      {/* Bottom hint bar */}
      <div className="flex items-center gap-4 px-4 py-2 bg-[#0d0d0d] border-t border-white/[0.05] shrink-0 text-[10px] text-on-surface-variant/40">
        <span>
          Call{' '}
          <code className="text-[#e53935]/70 font-mono">window.parent.postMessage({'{ passed: true }'}, '*')</code>
          {' '}when user completes the challenge
        </span>
        <span className="ml-auto hidden sm:block">sandbox="allow-scripts" · no localStorage · postMessage bridge only</span>
      </div>

    </div>
  );
}